<template>
    <div
        :id="id"
        v-click-away="hide"
        :class="['dropdown', {'active': shown, disabled, 'is-invalid': state === false}]"
        @mouseenter="mouseover = true"
        @mouseover="mouseover = true"
        @mouseleave="mouseover = false"
        @mouseout="mouseover = false"
    >
        <button
            :id="`${id}MenuButton`"
            class="btn btn-dropdown"
            type="button"
            aria-haspopup="true"
            :aria-expanded="shown ? 'true' : 'false'"
            @click="show($event)"
        >
            <slot name="prefix" />

            <input
                v-show="shown"
                ref="filterInput"
                v-model="filter"
                class="filter-control"
                type="text"
                :placeholder="selectionText"
                @blur="hide($event, mouseover)"
                @keydown.esc.tab="hide($event, false)"
            >

            <span
                v-show="!shown"
                class="selection mr-auto"
            >
                {{ selectionText }}
            </span>

            <span
                class="dropdown-toggle-icon"
            >
                <FontAwesomeIcon
                    icon="angle-down"
                    class="fa"
                />
            </span>
        </button>
        <div
            :class="['dropdown-menu', 'select', {'show': shown, 'multi': multiselect}]"
            :aria-labelledby="`${id}MenuButton`"
        >
            <p
                class="dropdown-header"
            >
                {{ dropdownHeader }}
            </p>

            <template v-if="orderedSections.length">
                <div
                    v-for="section in orderedSections"
                    :key="section.order"
                    class="dropdown-section"
                >
                    <div class="dropdown-section-header">
                        {{ section.text }}
                    </div>

                    <DropdownItem
                        v-for="(option, index) in orderedSections.filter(o => o.section === section.text)"
                        :key="index"
                        :text="option.text"
                        :value="option.value"
                        :classes="option.classes"
                        :selected="isSelected(option)"
                        @click="select($event)"
                    />
                </div>
            </template>
            <template v-else>
                <DropdownItem
                    v-for="(option, index) in filteredOptions"
                    :key="index"
                    :text="option.text"
                    :value="option.value"
                    :classes="option.classes"
                    :selected="isSelected(option)"
                    @click="select($event)"
                />
            </template>
        </div>
    </div>
</template>

<script>
    import {library} from '@fortawesome/fontawesome-svg-core';
    import {faAngleDown} from '@fortawesome/pro-solid-svg-icons';
    import {FontAwesomeIcon} from '@fortawesome/vue-fontawesome';
    import orderBy from 'lodash/orderBy';

    import {ClickAway} from '../directives/click-away';
    import DropdownItem from './DropdownItem.vue';

    library.add(
        faAngleDown,
    );

    /**
     * @group Inputs
     * The select replacement component as defined by UX style guide.
     */
    export default {
        name: 'Dropdown',
        components: {
            DropdownItem,
            FontAwesomeIcon,
        },
        directives: {
            'click-away': ClickAway,
        },
        model: {
            prop: 'selection',
            event: `kc::dropdown::selection`
        },
        props: {
            /**
             * This will allow deselecting a dropdown item if `multiselect` is not true
             */
            allowDeselect: {
                type: Boolean,
                default: false,
            },
            /**
             * The ID of the dropdown element
             */
            id: {
                type: String,
                required: true,
            },
            /**
             * The options objects to display in the dropdown that appears
             */
            options: {
                type: Array,
                required: false,
                // `[]`
                default() {
                    return [];
                },
            },
            /**
             * The sections objects to display items within the dropdown that appears
             */
            sections: {
                type: Array,
                required: false,
                // `[]`
                default() {
                    return [];
                },
            },
            /**
             * Sets the initially selected option. Must match one (or more if `multiselect` is `true`) of the option's `value` keys.
             */
            selected: {
                type: [String, Number, Array],
                required: false,
                // `''`
                default: '',
            },
            /**
             * The value of the dropdown. Used with `v-model`. Only has a single value in the array when `multiselect` prop is falsy.
             */
            selection: {
                type: Array,
                required: false,
                // `[]`
                default() {
                    return [];
                },
            },
            /**
             * Whether the dropdown is disabled, when `true` disables the ability to open or change the value selected and adds the `disabled` class to the dropdown element
             */
            disabled: {
                type: Boolean,
                required: false,
                // `false`
                default: false,
            },
            /**
             * Whether the dropdown allows multiple values to be selected
             */
            multiselect: {
                type: Boolean,
                required: false,
                // `false`
                default: false,
            },
            /**
             * The placeholder to be displayed when no selection is made as well as the placeholder of the filter input of the dropdown when no selection is made
             */
            placeholder: {
                type: String,
                required: false,
                // `'Select an option'`
                default: 'Select an option',
            },
            /**
             * Whether the input is in a valid or invalid state
             */
            state: {
                type: Boolean,
                required: false,
                default: null,
            },
        },
        data() {
            return {
                // the filter value
                filter: '',
                // whether or not the cursor is over the dropdown or any of it's children elements. used to know when to hide the dropdown on blur of the filter input or click on a selection
                mouseover: false,
                // whether or not the dropdown menu is shown
                shown: false,
            };
        },
        computed: {
            /**
             * Gets the options with the filter value applied.
             *
             * @returns Array of options with the filter applied
             */
            filteredOptions() {
                if (this.filter) {
                    return this.options.filter((option) => {
                        return option.text.toLowerCase().indexOf(this.filter.toLowerCase()) !== -1;
                    });
                }

                return this.options;
            },
            /**
             * Gets the options with the filter value applied.
             *
             * @returns Array of options with the filter applied
             */
            orderedSections() {
                return this.sections.length ? orderBy(this.sections, ['order']) : this.sections;
            },
            /**
             * Gets the selection text or placeholder for the default text or filter input placeholder.
             *
             * @returns String of the currently selected values or the placeholder prop
             */
            selectionText() {
                if (this.selection.length) {
                    return this.selection.map(selected => selected.text).join(', ');
                }

                return this.placeholder;
            },
            dropdownHeader() {
                if (this.options.length && !this.multiselect) {
                    return 'Select one';
                }

                if (this.options.length && this.multiselect) {
                    return 'Select one or multiple';
                }

                else {
                    return 'Found no items to display';
                }
            }
        },
        watch: {
            shown(isShown) {
                if (isShown) {
                    // Emitted when the dropdown is shown
                    this.$emit(`kc::dropdown::shown`);
                } else {
                    // Emitted when the dropdown is hidden
                    this.$emit(`kc::dropdown::hidden`);
                }
            },
        },
        created() {
            if (Array.isArray(this.selected)) {
                this.selected.forEach(selected => this.select(this.options.find(option => option.value === selected)));
            } else if (this.selected !== '') {
                this.select(this.options.find(option => option.value === this.selected));
            }
        },
        methods: {
            /**
             * Gets the index of the option provided based on the `value` keys.
             *
             * @param option: the object of the option from `options`
             * @returns {number} or -1 id not found in selection values
             */
            getSelectedIndex(option) {
                return this.selection.map((selected) => {
                    return selected.value;
                }).indexOf(option.value);
            },
            /**
             * Hides the dropdown if `mouseover` is false.
             *
             * @param event
             * @param mouseover: boolean of if the cursor is over the dropdown or children elements
             */
            hide(event, mouseover) {
                if (!mouseover) {
                    this.shown = false;
                }
            },
            /**
             * Checks if the provided option is in the current selection.
             *
             * @param option: the object of the option from `options`
             * @returns {boolean}
             */
            isSelected(option) {
                return this.getSelectedIndex(option) !== -1;
            },
            /**
             * @vuese
             * Toggles `shown` if the dropdown is not disabled and the event is not a phantom event and focuses on the filter input if the dropdown is shown.
             *
             * @param event
             */
            show(event) {
                if (!this.disabled) {
                    // event.screenX & event.screenY are a fix for the weird space key bug triggering a click - ????
                    if (event.screenX !== 0 && event.screenY !== 0 && event.target.type !== 'text') {
                        this.shown = !this.shown;
                        if (this.shown) {
                            this.$nextTick(() => {
                                this.$refs.filterInput.focus();
                            });
                        }
                    }
                }
            },
            /**
             * @vuese
             * Sets the `selection` to the provided selected or appends it when `multiselect` is `true`. Hides the dropdown if necessary.
             *
             * @param selected: the object of the option that was selected
             */
            select(selected) {
                let selection = this.selection,
                    currentIndex = this.getSelectedIndex(selected);

                if (this.multiselect) {
                    if (currentIndex === -1) {
                        selection.push(selected);
                    } else {
                        selection.splice(currentIndex, 1);
                    }
                } else {
                    if (this.allowDeselect && currentIndex >= 0) {
                        selection.splice(currentIndex, 1);
                    } else {
                        selection.splice(0, 1, selected);
                    }

                    this.mouseover = false;
                }

                // @vuese
                // Emitted when a dropdown item is selected
                this.$emit(`kc::dropdown::selection`, selection);
                this.hide({}, this.mouseover);
            },
        }
    };
</script>
